/**
 * How buffered rrweb events are packed into one request.
 *
 * Kept apart from `sessionRecorder` because these rules are pure and are the
 * part that has actually gone wrong in production: a snapshot silently dropped
 * here replays as a blank screen with 200s coming back the whole time, so the
 * logic is worth testing on its own rather than through the recorder.
 *
 * Section numbers refer to the backend team's "Session Recording — Frontend Guide".
 */

/**
 * rrweb's type for a full snapshot of the page. Replay starts from one of
 * these; everything else only describes changes to it (§5).
 */
export const FULL_SNAPSHOT = 2

/** Server rejects a chunk carrying more than 5000 events (§7.4). */
export const MAX_EVENTS_PER_CHUNK = 1000

/**
 * Ceiling, in RAW bytes, for a chunk carrying nothing but a full snapshot.
 *
 * The server's hard 2 MB limit applies to the request body ON THE WIRE, and
 * chunks travel gzipped (rrweb JSON compresses roughly 10x — repetitive DOM
 * and CSS text). So a raw snapshot far bigger than 2 MB is still sendable;
 * this cap only exists to stop something pathological (a runaway extension
 * injecting tens of MB) from being serialized and compressed at all. The
 * compressed result is checked against the real wire limit at send time.
 */
export const SOLO_CHUNK_MAX_BYTES = 8_000_000

export type BufferEntry = {event: {type: number} & Record<string, unknown>; bytes: number}

/** Non-fatal diagnostics. Never throws, never touches the DOM. */
const report = (message: string, detail?: unknown) => {
  if (import.meta.env.DEV) console.warn(`[session-recording] ${message}`, detail ?? '')
}

/**
 * Take as much of `source` as fits in one request, measured in BYTES not events
 * (§7.2.1). A single DOM mutation can carry an entire stylesheet — an
 * extension-injected one has been measured at 136 KB, and a whole chunk at
 * 1.27 MB — so an event count is not a proxy for payload size.
 *
 * Mutates `source`, removing what it took (and anything it dropped), and
 * reports the byte total removed so the caller's running count stays in step.
 * Entries stay wrapped so a batch can be handed back intact on a retry.
 *
 * `allowDrop` is false for the tab-hidden flush, whose budget is far smaller
 * than a whole request.
 */
export const takeBatchFrom = (
  source: BufferEntry[],
  budget: number,
  allowDrop: boolean
): {batch: BufferEntry[]; removedBytes: number} => {
  const batch: BufferEntry[] = []
  let size = 0
  let removedBytes = 0

  while (source.length && batch.length < MAX_EVENTS_PER_CHUNK) {
    const {bytes} = source[0]

    if (bytes > budget) {
      // A full snapshot is never junk, and it is the one event whose loss costs
      // the entire session rather than a moment of it. Before considering a
      // drop, give it a request of its own: "too big to share a chunk with
      // other events" is not the same as "too big to send", and the server
      // accepts a single chunk right up to 2 MB.
      if (allowDrop && source[0].event.type === FULL_SNAPSHOT && bytes <= SOLO_CHUNK_MAX_BYTES) {
        // Anything already gathered goes first, so the snapshot leads its own
        // chunk on the next pass rather than being buried mid-batch.
        if (batch.length) break

        return {batch: [source.shift() as BufferEntry], removedBytes: removedBytes + bytes}
      }

      // Too big to ever be sent. Retrying it would spend the five-strike budget
      // in §8.2 and end the session over a single event, so drop it and carry on.
      if (allowDrop) {
        const dropped = source.shift() as BufferEntry
        removedBytes += dropped.bytes

        // Dropping an ordinary event costs a moment of the replay. Dropping the
        // full snapshot costs the whole session — every later event edits a page
        // the player never received, so it renders blank (§12.1). That must not
        // be a debug-only line: it is the single most expensive thing this
        // module can do silently, so it warns in every build.
        if (dropped.event.type === FULL_SNAPSHOT) {
          console.warn(
            `[session-recording] dropped a ${dropped.bytes} byte FULL SNAPSHOT — ` +
              'this session will replay blank. It exceeded even a solo chunk ' +
              `(${SOLO_CHUNK_MAX_BYTES} bytes), so no request could carry it.`
          )
        } else {
          report(`dropped a ${dropped.bytes} byte event`, dropped.event.type)
        }
        continue
      }

      // The tab-hidden budget is far smaller than a whole request (§7.6), so
      // "does not fit" here does NOT mean "unsendable" — a full snapshot is
      // routinely bigger than 64 KB. Dropping it would leave the session with
      // no type 2 event and replay it as a blank screen (§12.1). Leave it in
      // the buffer: if the tab is only backgrounded, the next ordinary flush
      // sends it at full budget.
      break
    }

    if (batch.length && size + bytes > budget) break

    size += bytes
    removedBytes += bytes
    batch.push(source.shift() as BufferEntry)
  }

  return {batch, removedBytes}
}
